import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';

type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'NGN' | 'CAD' | 'AUD' | 'JPY' | 'CHF' | 'INR' | 'BRL' | 'ZAR' | 'AED' | 'CNY' | 'KRW' | 'SGD';

interface CurrencyInfo {
  code: CurrencyCode;
  symbol: string;
  name: string;
  decimals: number;
}

interface CurrencyContextType {
  currency: CurrencyCode;
  setCurrency: (code: CurrencyCode) => void;
  currencies: CurrencyInfo[];
  rates: Record<string, number>;
  ratesLoading: boolean;
  /** Convert a USD value to the user's selected currency */
  convert: (usdAmount: number) => number;
  /** Format a USD value as the user's selected currency string */
  format: (usdAmount: number, opts?: { compact?: boolean; decimals?: number }) => string;
  /** Get the symbol for the current currency */
  currencySymbol: string;
}

const CURRENCIES: CurrencyInfo[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar', decimals: 2 },
  { code: 'EUR', symbol: '\u20AC', name: 'Euro', decimals: 2 },
  { code: 'GBP', symbol: '\u00A3', name: 'British Pound', decimals: 2 },
  { code: 'NGN', symbol: '\u20A6', name: 'Nigerian Naira', decimals: 0 },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar', decimals: 2 },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', decimals: 2 },
  { code: 'JPY', symbol: '\u00A5', name: 'Japanese Yen', decimals: 0 },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', decimals: 2 },
  { code: 'INR', symbol: '\u20B9', name: 'Indian Rupee', decimals: 0 },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', decimals: 2 },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand', decimals: 2 },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham', decimals: 2 },
  { code: 'CNY', symbol: '\u00A5', name: 'Chinese Yuan', decimals: 2 },
  { code: 'KRW', symbol: '\u20A9', name: 'South Korean Won', decimals: 0 },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', decimals: 2 },
];

const STORAGE_KEY = 'acebarter-currency';
const RATES_CACHE_KEY = 'acebarter-fx-rates';
const RATES_TTL = 30 * 60 * 1000; // 30 minutes

function getInitialCurrency(): CurrencyCode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as CurrencyCode | null;
    if (stored && CURRENCIES.some(c => c.code === stored)) return stored;
  } catch {}
  return 'USD';
}

function getCachedRates(): { rates: Record<string, number>; ts: number } | null {
  try {
    const raw = localStorage.getItem(RATES_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.ts && Date.now() - parsed.ts < RATES_TTL) return parsed;
    }
  } catch {}
  return null;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currency, setCurrencyRaw] = useState<CurrencyCode>(getInitialCurrency);
  const [rates, setRates] = useState<Record<string, number>>({ USD: 1 });
  const [ratesLoading, setRatesLoading] = useState(false);

  const dbSaveRef = useRef(false);

  const setCurrency = useCallback((code: CurrencyCode) => {
    setCurrencyRaw(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch {}
    // Persist to database
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        supabase.from('users').update({ preferred_currency: code }).eq('id', user.id).then(() => {});
      }
    });
  }, []);

  // Load from database on mount (if user is logged in)
  useEffect(() => {
    if (dbSaveRef.current) return;
    dbSaveRef.current = true;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase.from('users').select('preferred_currency').eq('id', user.id).maybeSingle().then(({ data }) => {
        if (data?.preferred_currency && CURRENCIES.some(c => c.code === data.preferred_currency)) {
          setCurrencyRaw(data.preferred_currency as CurrencyCode);
          try { localStorage.setItem(STORAGE_KEY, data.preferred_currency); } catch {}
        }
      });
    });
  }, []);

  // Fetch exchange rates
  useEffect(() => {
    const cached = getCachedRates();
    if (cached) {
      setRates(cached.rates);
      return;
    }

    setRatesLoading(true);
    fetch('https://api.exchangerate-api.com/v4/latest/USD')
      .then(res => res.json())
      .then(data => {
        if (data?.rates) {
          setRates(data.rates);
          try {
            localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ rates: data.rates, ts: Date.now() }));
          } catch {}
        }
      })
      .catch(() => {
        // Fallback rates if API fails
        setRates({
          USD: 1, EUR: 0.92, GBP: 0.79, NGN: 1550, CAD: 1.36,
          AUD: 1.53, JPY: 149, CHF: 0.88, INR: 83.5, BRL: 5.0,
          ZAR: 18.5, AED: 3.67, CNY: 7.24, KRW: 1340, SGD: 1.34,
        });
      })
      .finally(() => setRatesLoading(false));
  }, []);

  const currencyInfo = useMemo(() => CURRENCIES.find(c => c.code === currency) || CURRENCIES[0], [currency]);

  const convert = useCallback((usdAmount: number): number => {
    const rate = rates[currency] || 1;
    return usdAmount * rate;
  }, [currency, rates]);

  const format = useCallback((usdAmount: number, opts?: { compact?: boolean; decimals?: number }): string => {
    // Guard against invalid values
    if (usdAmount == null || !isFinite(usdAmount) || isNaN(usdAmount)) {
      return `${currencyInfo.symbol}0`;
    }

    const converted = convert(usdAmount);
    const decimals = opts?.decimals ?? currencyInfo.decimals;

    if (opts?.compact && Math.abs(converted) >= 1000) {
      const absVal = Math.abs(converted);
      const sign = converted < 0 ? '-' : '';
      if (absVal >= 1e9) return `${sign}${currencyInfo.symbol}${(absVal / 1e9).toFixed(1)}B`;
      if (absVal >= 1e6) return `${sign}${currencyInfo.symbol}${(absVal / 1e6).toFixed(1)}M`;
      if (absVal >= 1e3) return `${sign}${currencyInfo.symbol}${(absVal / 1e3).toFixed(1)}K`;
    }

    return `${currencyInfo.symbol}${converted.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  }, [convert, currencyInfo]);

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        currencies: CURRENCIES,
        rates,
        ratesLoading,
        convert,
        format,
        currencySymbol: currencyInfo.symbol,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used within CurrencyProvider');
  return context;
};
